// The unload-safe path uses `fetch(keepalive)` so the request outlives the page and still
// carries the Authorization header, which `sendBeacon` cannot set.
import { Injectable, inject } from '@angular/core';
import { LstUserService } from '@savvaslearning/lst-auth';

import { environment } from '../../../../environments/environment';
import { ClassviewServiceClient } from '../../http/classview-service-client';
import { DEVICE_ID_HEADER, DeviceIdentityService } from '../device-identity/device-identity.service';

import type { BulkLogApiService } from './bulk-log-api.service';
import type { BulkLogBatch } from './bulk-log.model';

@Injectable({ providedIn: 'root' })
export class ClassviewBulkLogApi implements BulkLogApiService {
  private readonly client = inject(ClassviewServiceClient);
  private readonly lstUserService = inject(LstUserService, { optional: true });
  private readonly deviceIdentity = inject(DeviceIdentityService);

  async flush(batch: BulkLogBatch): Promise<void> {
    if (!this.lstUserService?.getRbsToken?.()) {
      throw new Error('Cannot flush activity logs without an RBS bearer token.');
    }
    await this.client.post('/v1/bulk-log', batch);
  }

  flushUnloadSafe(batch: BulkLogBatch): boolean {
    if (typeof fetch !== 'function') return false;
    const token = this.lstUserService?.getRbsToken?.() ?? '';
    if (!token) return false;
    try {
      // keepalive lets the request outlive the page (like sendBeacon) while still
      // sending the Authorization header the new backend requires. Fire-and-forget:
      // there is no retry once the page is unloading. Raw fetch bypasses HttpClient
      // (and so the deviceIdentityInterceptor) entirely, so X-Device-Id is set by hand
      // here — otherwise this would be a call the interceptor never sees, and so never
      // attaches device identity to.
      void fetch(`${environment.classviewServiceUrl}/v1/bulk-log`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${token}`,
          [DEVICE_ID_HEADER]: this.deviceIdentity.deviceId(),
        },
        body: JSON.stringify(batch),
      }).catch(() => undefined);
      return true;
    } catch {
      return false;
    }
  }
}
